/**
 * Custom hook for Irrigation System Monitoring logic
 * Handles irrigation activity detection, weekly performance calculations, and data refresh
 */

const React = require('react');
const { useState, useEffect, useCallback } = React;
const dayjs = require('../../../utils/dayjsConfig');

const IRRIGATION_ACTIVE_WINDOW_HOURS = 3.5;
const REFRESH_INTERVAL_MS = 3600000; // 1 hour in milliseconds
const WEEKLY_PERFORMANCE_PLAN_POSITION = 0.75; // 75% of bar width
const WEEKLY_PERFORMANCE_TOTAL_MULTIPLIER = 1.25; // Total bar width = plannedValue * 1.25
const DEFAULT_BAR_WIDTH_MM = 75;
const DEFAULT_BAR_WIDTH_INCH = 3;
const DEFAULT_BAR_WIDTH_HOURS = 75;

/**
 * Determines if irrigation is currently active based on end date
 * @param {number} irrigationEndDate - Unix timestamp
 * @returns {boolean}
 */
const isCurrentlyIrrigating = (irrigationEndDate) => {
  if (!irrigationEndDate) return false;

  const now = dayjs();
  const endDate = dayjs.unix(irrigationEndDate);
  const hoursSinceEnd = now.diff(endDate, 'hours', true);

  return hoursSinceEnd >= 0 && hoursSinceEnd <= IRRIGATION_ACTIVE_WINDOW_HOURS;
};

/**
 * Formats irrigation end date for display
 * @param {number} irrigationEndDate - Unix timestamp
 * @returns {string} - "Today XX:XXam/pm", "Yesterday...", or "X days ago"
 */
const formatIrrigationTime = (irrigationEndDate) => {
  if (!irrigationEndDate) return 'N/A';

  const now = dayjs();
  const endDate = dayjs.unix(irrigationEndDate);
  const daysDiff = now.diff(endDate, 'days');

  if (daysDiff === 0) {
    return `Today ${endDate.format('h:mma')}`;
  } else if (daysDiff === 1) {
    return `Yesterday ${endDate.format('h:mma')}`;
  } else {
    return `${daysDiff} days ago`;
  }
};

/**
 * Gets the start of the current week based on user's regional settings
 * @param {string} plannerType - User's planner type from settings
 * @returns {number} Unix timestamp of week start
 */
const getWeekStartDate = (plannerType = 'weekly') => {
  const now = dayjs();
  // For weekly planner, week starts on Sunday (0)
  // For daily or other types, use Monday (1)
  const startOfWeek = plannerType === 'weekly' ? 0 : 1;

  return now.startOf('week').day(startOfWeek).unix();
};

/**
 * Calculates weekly performance metrics
 * @param {number} actualValue - Actual irrigation amount
 * @param {number} dailyReturn - Daily return value
 * @param {string} unit - Unit of measurement
 * @returns {Object} Weekly performance data
 */
const calculateWeeklyPerformance = (actualValue, dailyReturn, unit = 'mm') => {
  let plannedValue = dailyReturn ? dailyReturn * 7 : 0;
  let totalBarWidth;

  if (plannedValue > 0) {
    totalBarWidth = plannedValue * WEEKLY_PERFORMANCE_TOTAL_MULTIPLIER;
  } else {
    // Use default constant if no plan exists
    switch (unit) {
      case 'inch':
        totalBarWidth = DEFAULT_BAR_WIDTH_INCH;
        break;
      case 'hours':
        totalBarWidth = DEFAULT_BAR_WIDTH_HOURS;
        break;
      default:
        totalBarWidth = DEFAULT_BAR_WIDTH_MM;
    }
  }

  // Calculate percentage, max at 100%
  const percentage = Math.min((actualValue / totalBarWidth) * 100, 100);

  // Planned value position is at 80% of total bar width
  const plannedPosition = WEEKLY_PERFORMANCE_PLAN_POSITION * 100;

  return {
    actualValue,
    plannedValue,
    totalBarWidth,
    percentage,
    plannedPosition,
    unit,
  };
};

/**
 * Gets irrigation source priority
 * Priority: flow_meter > manual_input > connect_sensor
 * @param {Object} field - Field data
 * @returns {string} Source type
 */
const getIrrigationSource = (field) => {
  // Check for flow meter (e-piezo, Talgil, etc.)
  if (field.flowMeterData && field.flowMeterData.lastReading) {
    return 'flow_meter';
  }

  // Check for manual inputs
  if (field.manualIrrigationData && field.manualIrrigationData.lastEntry) {
    return 'manual_input';
  }

  // Use Connect sensor only for flood fields if no flow meter
  if (field.irrigationType === 'flood' && field.connectSensorData) {
    return 'connect_sensor';
  }

  return 'unknown';
};

/**
 * Gets irrigation end date based on source priority
 * @param {Object} field - Field data
 * @returns {number} Unix timestamp
 */
const getIrrigationEndDate = (field) => {
  const source = getIrrigationSource(field);

  switch (source) {
    case 'flow_meter':
      return field.flowMeterData?.endDate || 0;
    case 'manual_input':
      return field.manualIrrigationData?.endDate || 0;
    case 'connect_sensor':
      return field.connectSensorData?.endDate || 0;
    default:
      return 0;
  }
};

/**
 * Main hook for irrigation monitoring logic
 * @param {Array} fields - Array of field data
 * @param {string} plannerType - User's planner type
 * @returns {Object} Monitoring data and refresh function
 */
const useIrrigationMonitoring = (fields = [], plannerType = 'weekly') => {
  const [monitoringData, setMonitoringData] = useState([]);
  const [lastRefresh, setLastRefresh] = useState(Date.now());

  /**
   * Processes field data to generate monitoring data
   */
  const processFields = useCallback(() => {
    if (!fields || fields.length === 0) {
      setMonitoringData([]);
      return;
    }

    const weekStartDate = getWeekStartDate(plannerType);
    const now = dayjs();

    const processed = fields.map((field) => {
      const irrigationEndDate = getIrrigationEndDate(field);
      const source = getIrrigationSource(field);
      const isIrrigating = isCurrentlyIrrigating(irrigationEndDate);

      // Calculate actual irrigation value for the week
      const weekStart = dayjs.unix(weekStartDate);
      const irrigationEvents = field.irrigationHistory || [];
      const actualValue = irrigationEvents
        .filter((event) => dayjs.unix(event.date).isAfter(weekStart))
        .reduce((sum, event) => sum + (event.amount || 0), 0);

      // Calculate weekly performance
      const weeklyPerformance = calculateWeeklyPerformance(
        actualValue,
        field.dailyReturn,
        field.unit || 'mm',
      );

      return {
        fieldId: field.id,
        fieldName: field.name,
        irrigationActivity: {
          fieldId: field.id,
          source,
          irrigationEndDate,
          isIrrigating,
          displayText: formatIrrigationTime(irrigationEndDate),
          lastUpdated: now.unix(),
        },
        weeklyPerformance: {
          ...weeklyPerformance,
          fieldId: field.id,
          weekStartDate,
        },
      };
    });

    setMonitoringData(processed);
    setLastRefresh(Date.now());
  }, [fields, plannerType]);

  /**
   * Manual refresh function
   */
  const refresh = useCallback(() => {
    processFields();

    // Track refresh in Mixpanel
    if (typeof mixpanel !== 'undefined' && mixpanel && mixpanel.track) {
      mixpanel.track('Farm View Refreshed', {
        field_count: fields?.length || 0,
        refresh_type: 'Manual',
      });
    }
  }, [processFields, fields]);

  // Initial processing
  useEffect(() => {
    processFields();
  }, [processFields]);

  // Auto-refresh every hour
  useEffect(() => {
    const timerId = setInterval(() => {
      processFields();

      // Track auto-refresh in Mixpanel
      if (typeof mixpanel !== 'undefined' && mixpanel && mixpanel.track) {
        mixpanel.track('Farm View Refreshed', {
          field_count: fields?.length || 0,
          refresh_type: 'Auto',
        });
      }
    }, REFRESH_INTERVAL_MS);

    return () => clearInterval(timerId);
  }, [processFields, fields]);

  /**
   * Get monitoring data for a specific field
   * @param {string} fieldId
   * @returns {Object|null}
   */
  const getFieldData = useCallback(
    (fieldId) => {
      return monitoringData.find((data) => data.fieldId === fieldId) || null;
    },
    [monitoringData],
  );

  return {
    monitoringData,
    lastRefresh,
    refresh,
    getFieldData,
  };
};

module.exports = useIrrigationMonitoring;
